import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import { 
  Plus, Search, Edit2, Trash2, Wrench, Clock, CheckCircle, 
  XCircle, AlertCircle, User, Building2, MapPin, Calendar,
  Phone, Mail, FileText, Eye, History, Printer, Download,
  Monitor, Network, Server, Camera, Projector, Tablet,
  Battery, Scale, Tv, Radio, HelpCircle, DollarSign, X
} from 'lucide-react';
import { 
  technicalServices, serviceStatusHistory, companies, locations, 
  suppliers, currentUser, deviceCategories 
} from '../data/mockData';
import { TechnicalService, ServiceStatusHistory } from '../types';
import Modal from './Modal';

const TechnicalServicePage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingService, setEditingService] = useState<TechnicalService | null>(null);
  const [selectedService, setSelectedService] = useState<TechnicalService | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [showSupplierHistoryModal, setShowSupplierHistoryModal] = useState(false);
  const [selectedServiceForHistory, setSelectedServiceForHistory] = useState<TechnicalService | null>(null);
  const [supplierHistory, setSupplierHistory] = useState<any>(null);
  const [showServiceHistoryModal, setShowServiceHistoryModal] = useState(false);
  const [serviceHistory, setServiceHistory] = useState<any>(null);
  const [servicesData, setServicesData] = useState<TechnicalService[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [suppliersData, setSuppliersData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [deviceSuggestions, setDeviceSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState('');
  const [supplierQuoteStatus, setSupplierQuoteStatus] = useState<{[key: number]: boolean}>({});
  const [printersData, setPrintersData] = useState<any[]>([]);
  const [mobilePhonesData, setMobilePhonesData] = useState<any[]>([]);
  const [handheldTerminalsData, setHandheldTerminalsData] = useState<any[]>([]);
  const [computersData, setComputersData] = useState<any[]>([]);
  const [mobilePhoneSearchTerm, setMobilePhoneSearchTerm] = useState<{[key: number]: string}>({});
  const [mobilePhoneSearchResults, setMobilePhoneSearchResults] = useState<{[key: number]: any[]}>({});
  const [computerSearchTerm, setComputerSearchTerm] = useState<{[key: number]: string}>({});
  const [computerSearchResults, setComputerSearchResults] = useState<{[key: number]: any[]}>({});
  const [radioLookupStatus, setRadioLookupStatus] = useState<{[key: number]: 'loading' | 'found' | 'not-found' | null}>({});
  const radioLookupTimerRef = useRef<{[key: number]: ReturnType<typeof setTimeout>}>({});
  const [radiosData, setRadiosData] = useState<any[]>([]);
  const [scalesData, setScalesData] = useState<any[]>([]);
  const [networkEquipmentData, setNetworkEquipmentData] = useState<any[]>([]);
  const [physicalServersData, setPhysicalServersData] = useState<any[]>([]);
  const [camerasData, setCamerasData] = useState<any[]>([]);
  const [iotDevicesData, setIotDevicesData] = useState<any[]>([]);
  const [serialSuggestions, setSerialSuggestions] = useState<{[key: number]: any[]}>({});
  const serialSearchTimerRef = useRef<{[key: number]: ReturnType<typeof setTimeout>}>({});
  const [showLabelSettingsModal, setShowLabelSettingsModal] = useState(false);
  const [rejectModalService, setRejectModalService] = useState<TechnicalService | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [labelSettings, setLabelSettings] = useState({
    width: 100,
    height: 75,
    orientation: 'portrait',
    fontSize: 8,
    margin: 2
  });
  
  const [formData, setFormData] = useState({
    sendEmailToContact: false,
    sendEmailToTechnician: false,
    sendEmailToSupplier: false
  });
  
  const [serviceData, setServiceData] = useState({
    service_number: '',
    company_id: 0,
    location_id: 0,
    fault_description: '',
    status: 'yeni' as TechnicalService['status'],
    priority: 'normal' as TechnicalService['priority'],
    contact_person_id: 0,
    supplier_id: 0,
    assigned_technician_id: 0,
    estimated_completion: '',
    notes: ''
  });
  
  const [devices, setDevices] = useState([
    {
      device_category: '',
      device_name: '',
      inventory_number: '',
      serial_number: '',
      fault_description: '',
      mobile_phone_id: null as number | null,
      computer_id: null as number | null,
      linked_device_type: null as string | null,
      linked_device_id: null as number | null
    }
  ]);
  
  const [expandedDevices, setExpandedDevices] = useState<{[key: number]: boolean}>({ 0: true });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir teknik servis açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'technical-service' || !e.detail?.entityId) return;
      try {
        const service = await apiService.getTechnicalServiceById(e.detail.entityId);
        if (service) { setSelectedService(service); setShowDetailModal(true); }
      } catch (err) { console.error('[openEntityById technicalservice]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Admin/superadmin tüm kullanıcıları görebilir, teknisyen sadece teknisyen rolündeki kullanıcıları görür
      let usersPromise: Promise<any>;
      if (['admin', 'superadmin'].includes(user?.role || '')) {
        usersPromise = apiService.getUsers();
      } else if (user?.role === 'teknisyen') {
        usersPromise = apiService.getUsers('teknisyen');
      } else {
        usersPromise = Promise.resolve([]);
      }
        
      const [servicesResponse, companiesResponse, locationsResponse, usersResponse] = await Promise.all([
        apiService.getTechnicalServices(),
        apiService.getCompanies(),
        apiService.getLocations(),
        usersPromise
      ]);
      
      // Get technical service suppliers separately
      const techSuppliersResponse = await fetch(`${import.meta.env.VITE_API_URL}/technical-service/suppliers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const techSuppliers = techSuppliersResponse.ok ? await techSuppliersResponse.json() : [];
      
      const printersResponse = await fetch(`${import.meta.env.VITE_API_URL}/printers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const printers = printersResponse.ok ? await printersResponse.json() : [];

      const mobilePhonesResponse = await fetch(`${import.meta.env.VITE_API_URL}/mobile-phones`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const mobilePhones = mobilePhonesResponse.ok ? await mobilePhonesResponse.json() : [];

      const handheldTerminalsResponse = await apiService.getHandheldTerminals();
      const handheldTerminals = Array.isArray(handheldTerminalsResponse) ? handheldTerminalsResponse : [];

      const computersResponse = await fetch(`${import.meta.env.VITE_API_URL}/computers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const computers = computersResponse.ok ? await computersResponse.json() : [];

      setServicesData(servicesResponse);
      setCompaniesData(companiesResponse);
      
      // Lokasyon verilerini güvenli şekilde işle
      let locations = [];
      if (locationsResponse && Array.isArray(locationsResponse.data)) {
        locations = locationsResponse.data;
      } else if (Array.isArray(locationsResponse)) {
        locations = locationsResponse;
      } else {
        console.warn('Unexpected locations response format:', locationsResponse);
        locations = [];
      }
      setLocationsData(locations);
      
      // Lokasyon yükleme hatası kontrolü
      if (locations.length === 0) {
        console.warn('No locations loaded, this might cause issues in the form');
      }
      setSuppliersData(techSuppliers);
      setUsersData(usersResponse);
      setPrintersData(printers);
      setMobilePhonesData(mobilePhones);
      setHandheldTerminalsData(handheldTerminals);
      setComputersData(computers);

      const radiosResponse = await fetch(`${import.meta.env.VITE_API_URL}/radios`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const radiosJson = radiosResponse.ok ? await radiosResponse.json() : [];
      setRadiosData(Array.isArray(radiosJson) ? radiosJson : (radiosJson.data || []));

      const scalesResponse = await fetch(`${import.meta.env.VITE_API_URL}/scales`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const scalesJson = scalesResponse.ok ? await scalesResponse.json() : [];
      setScalesData(Array.isArray(scalesJson) ? scalesJson : (scalesJson.data || []));

      const networkResponse = await fetch(`${import.meta.env.VITE_API_URL}/network`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const networkJson = networkResponse.ok ? await networkResponse.json() : [];
      setNetworkEquipmentData(Array.isArray(networkJson) ? networkJson : (networkJson.data || []));

      const physicalServersResponse = await fetch(`${import.meta.env.VITE_API_URL}/servers/physical`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const physicalServersJson = physicalServersResponse.ok ? await physicalServersResponse.json() : [];
      setPhysicalServersData(Array.isArray(physicalServersJson) ? physicalServersJson : (physicalServersJson.data || []));

      const camerasResponse = await fetch(`${import.meta.env.VITE_API_URL}/cameras`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const camerasJson = camerasResponse.ok ? await camerasResponse.json() : [];
      setCamerasData(Array.isArray(camerasJson) ? camerasJson : (camerasJson.data || []));

      const iotResponse = await fetch(`${import.meta.env.VITE_API_URL}/iot`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const iotJson = iotResponse.ok ? await iotResponse.json() : [];
      setIotDevicesData(Array.isArray(iotJson) ? iotJson : (iotJson.data || []));

      // Tedarikçisi olan servisler için teklif durumunu kontrol et
      const quoteStatuses: {[key: number]: boolean} = {};
      for (const service of servicesResponse) {
        if (service.supplier_id) {
          try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/technical-service/${service.id}/supplier-quote-status`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
              }
            });
            if (response.ok) {
              const { hasQuote } = await response.json();
              quoteStatuses[service.id] = hasQuote;
            }
          } catch (error) {
            console.error(`Error checking quote status for service ${service.id}:`, error);
          }
        }
      }
      setSupplierQuoteStatus(quoteStatuses);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('techservice.notifLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const iconComponents = {
    Monitor, Printer: FileText, Phone, Network, Server, Camera, 
    Projector, Tablet, Battery, Scale, Tv, Radio, HelpCircle
  };

  // Tüm status seçenekleri (görüntüleme için)
  const allStatusOptions = [
    { value: 'servis-kaydi-olusturuldu', label: t('techservice.statusCreated'), color: 'bg-primary/20 text-primary-dark' },
    { value: 'servise-gonderildi', label: t('techservice.statusSent'), color: 'bg-purple-100 text-purple-800' },
    { value: 'isleme-alindi', label: t('techservice.statusProcessing'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'teklif-yuklendi', label: t('techservice.statusQuoteUploaded'), color: 'bg-orange-100 text-orange-800' },
    { value: 'siparis-olusturuldu', label: t('techservice.statusOrderCreated'), color: 'bg-indigo-100 text-indigo-800' },
    { value: 'onarildi-gonderildi', label: t('techservice.statusRepairedSent'), color: 'bg-cyan-100 text-cyan-800' },
    { value: 'tamamlandi', label: t('techservice.statusCompleted'), color: 'bg-green-100 text-green-800' },
    { value: 'iptal-edildi', label: t('techservice.statusCancelled'), color: 'bg-red-100 text-red-800' },
    { value: 'reddedildi', label: t('techservice.statusRejected'), color: 'bg-red-200 text-red-900' }
  ];

  // Filtre için kullanılan status seçenekleri (sadece kullanılanlar)
  const filterStatusOptions = [
    { value: 'servis-kaydi-olusturuldu', label: t('techservice.statusCreated') },
    { value: 'servise-gonderildi', label: t('techservice.statusSent') },
    { value: 'isleme-alindi', label: t('techservice.statusProcessing') },
    { value: 'teklif-yuklendi', label: t('techservice.statusQuoteUploaded') },
    { value: 'siparis-olusturuldu', label: t('techservice.statusOrderCreated') },
    { value: 'onarildi-gonderildi', label: t('techservice.statusRepairedSent') },
    { value: 'tamamlandi', label: t('techservice.statusCompleted') },
    { value: 'iptal-edildi', label: t('techservice.statusCancelled') },
    { value: 'reddedildi', label: t('techservice.statusRejected') }
  ];

  // Normal kullanıcılar için status seçenekleri
  const statusOptions = [
    { value: 'servis-kaydi-olusturuldu', label: t('techservice.statusCreated'), color: 'bg-primary/20 text-primary-dark' },
    { value: 'servise-gonderildi', label: t('techservice.statusSent'), color: 'bg-purple-100 text-purple-800' },
    { value: 'siparis-olusturuldu', label: t('techservice.statusOrderCreated'), color: 'bg-indigo-100 text-indigo-800' },
    { value: 'onarildi-gonderildi', label: t('techservice.statusRepairedSent'), color: 'bg-cyan-100 text-cyan-800' },
    { value: 'tamamlandi', label: t('techservice.statusCompleted'), color: 'bg-green-100 text-green-800' },
    { value: 'iptal-edildi', label: t('techservice.statusCancelled'), color: 'bg-red-100 text-red-800' }
  ];

  const priorityOptions = [
    { value: 'dusuk', label: t('techservice.priorityLow'), color: 'text-gray-600 dark:text-gray-400' },
    { value: 'normal', label: t('techservice.priorityNormal'), color: 'text-primary' },
    { value: 'yuksek', label: t('techservice.priorityHigh'), color: 'text-orange-600' },
    { value: 'acil', label: t('techservice.priorityUrgent'), color: 'text-red-600' }
  ];

  // Status ve priority helper fonksiyonları
  const getStatusLabel = (status: string) => {
    const statusOption = allStatusOptions.find(option => option.value === status);
    return statusOption ? statusOption.label : status;
  };

  const getStatusColor = (status: string) => {
    const statusOption = allStatusOptions.find(option => option.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getPriorityLabel = (priority: string) => {
    const priorityOption = priorityOptions.find(option => option.value === priority);
    return priorityOption ? priorityOption.label : priority;
  };

  const getPriorityColor = (priority: string) => {
    const priorityOption = priorityOptions.find(option => option.value === priority);
    return priorityOption ? priorityOption.color : 'text-gray-600 dark:text-gray-400';
  };

  const filteredServices = servicesData.filter(service => {
    // Tamamlanan ve reddedilen kayıtları ana sayfada gösterme (sadece ilgili filtre seçildiğinde göster)
    if (filterStatus === 'all' && (service.status === 'tamamlandi' || service.status === 'reddedildi')) {
      return false;
    }
    
    const searchLower = String(searchTerm || '').toLowerCase();
    const matchesSearch = String(service.service_number || '').toLowerCase().includes(searchLower) ||
                         String(service.device_name || '').toLowerCase().includes(searchLower) ||
                         String(service.fault_description || '').toLowerCase().includes(searchLower) ||
                         service.company?.name.toLowerCase().includes(searchLower) ||
                         service.location?.name.toLowerCase().includes(searchLower) ||
                         (service.serial_number && String(service.serial_number || '').toLowerCase().includes(searchLower)) ||
                         (service.inventory_number && String(service.inventory_number || '').toLowerCase().includes(searchLower)) ||
                         service.contact_person?.name.toLowerCase().includes(searchLower);
    const matchesStatus = filterStatus === 'all' || service.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || service.priority === filterPriority;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleEdit = async (service: TechnicalService) => {
    setEditingService(service);
    setServiceData({
      service_number: service.service_number,
      company_id: service.company_id,
      location_id: service.location_id,
      fault_description: service.fault_description,
      status: service.status,
      priority: service.priority,
      contact_person_id: service.contact_person_id,
      supplier_id: service.supplier_id || 0,
      assigned_technician_id: service.assigned_technician_id || 0,
      estimated_completion: service.estimated_completion ? service.estimated_completion.split('T')[0] : '',
      notes: service.notes || ''
    });
    
    // Çoklu cihaz varsa service_devices'dan yükle
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/technical-service/${service.id}/devices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const serviceDevices = await response.json();
        if (serviceDevices.length > 0) {
          // Çoklu cihaz var
          setDevices(serviceDevices.map(device => ({
            device_category: device.device_category || '',
            device_name: device.device_name,
            inventory_number: device.inventory_number || '',
            serial_number: device.serial_number || '',
            fault_description: device.fault_description || '',
            mobile_phone_id: device.mobile_phone_id || null,
            computer_id: device.computer_id || null,
            linked_device_type: device.linked_device_type || null,
            linked_device_id: device.linked_device_id || null
          })));
          setExpandedDevices({ 0: true });
        } else {
          // Tek cihaz (eski sistem)
          setDevices([{
            device_category: service.device_category || '',
            device_name: service.device_name,
            inventory_number: service.inventory_number || '',
            serial_number: service.serial_number || '',
            fault_description: service.fault_description,
            mobile_phone_id: service.mobile_phone_id || null,
            computer_id: service.computer_id || null,
            linked_device_type: null,
            linked_device_id: null
          }]);
          setExpandedDevices({ 0: true });
        }
      }
    } catch (error) {
      // Hata durumunda eski sistem
      setDevices([{
        device_category: service.device_category || '',
        device_name: service.device_name,
        inventory_number: service.inventory_number || '',
        serial_number: service.serial_number || '',
        fault_description: service.fault_description,
        mobile_phone_id: service.mobile_phone_id || null,
        computer_id: service.computer_id || null,
        linked_device_type: null,
        linked_device_id: null
      }]);
      setExpandedDevices({ 0: true });
    }
    
    setShowModal(true);
  };

  const calculateBusinessDays = (startDate: Date, days: number) => {
    let count = 0;
    let currentDate = new Date(startDate);
    
    while (count < days) {
      currentDate.setDate(currentDate.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        count++;
      }
    }
    
    return currentDate.toISOString().split('T')[0];
  };

  const handleAdd = () => {
    setEditingService(null);
    // Son servis numarasından sonraki numarayı hesapla
    const currentYear = new Date().getFullYear();
    const currentYearServices = servicesData.filter(s => s.service_number.includes(`TS-${currentYear}-`));
    let maxNumber = 0;
    
    currentYearServices.forEach(service => {
      const match = service.service_number.match(/TS-\d{4}-(\d{3})/);
      if (match) {
        const num = parseInt(match[1]);
        if (num > maxNumber) maxNumber = num;
      }
    });
    
    const nextServiceNumber = `TS-${currentYear}-${String(maxNumber + 1).padStart(3, '0')}`;
    const estimatedDate = calculateBusinessDays(new Date(), 10); // 2 hafta = 10 iş günü
    
    setServiceData({
      service_number: nextServiceNumber,
      company_id: 0,
      location_id: user?.location_id || 0,
      fault_description: '',
      status: 'servis-kaydi-olusturuldu',
      priority: 'normal',
      contact_person_id: 0,
      supplier_id: 0,
      assigned_technician_id: user?.id || 0,
      estimated_completion: estimatedDate,
      notes: ''
    });
    
    // Tek boş cihaz ile başla
    setDevices([{
      device_category: '',
      device_name: '',
      inventory_number: '',
      serial_number: '',
      fault_description: '',
      mobile_phone_id: null,
      computer_id: null,
      linked_device_type: null,
      linked_device_id: null
    }]);

    setShowModal(true);
  };

  const searchDeviceHistory = async (query: string) => {
    if (query.length < 2) {
      setDeviceSuggestions([]);
      return;
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/technical-service/device-history/search?query=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const suggestions = await response.json();
        setDeviceSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Device search error:', error);
    }
  };

  const handleDeviceSuggestionSelect = (suggestion: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: suggestion.device_name,
      device_category: suggestion.device_category || '',
      inventory_number: suggestion.inventory_number || '',
      serial_number: suggestion.serial_number || ''
    };
    setDevices(updatedDevices);
    setDeviceSuggestions([]);
  };

  const handlePrinterSelect = (printer: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: `${printer.brand} ${printer.model}`,
      device_category: 'yazici',
      inventory_number: '',
      serial_number: printer.serial_number || '',
      linked_device_type: 'printer',
      linked_device_id: printer.id
    };
    setDevices(updatedDevices);
  };

  const handleRadioSelect = (radio: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: `${radio.brand} ${radio.model}`,
      device_category: 'telsiz',
      inventory_number: radio.inventory_number || '',
      serial_number: radio.serial_number || '',
      linked_device_type: 'radio',
      linked_device_id: radio.id
    };
    setDevices(updatedDevices);
  };

  const handleScaleSelect = (scale: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: `${scale.brand} ${scale.model}`,
      device_category: 'terazi',
      inventory_number: scale.inventory_number || '',
      serial_number: scale.serial_number || '',
      linked_device_type: 'scale',
      linked_device_id: scale.id
    };
    setDevices(updatedDevices);
  };

  const handleNetworkEquipmentSelect = (equipment: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: equipment.switch_name || `${equipment.brand} ${equipment.model}`,
      device_category: 'ag-ekipmani',
      inventory_number: equipment.inventory_number || '',
      serial_number: equipment.serial_number || '',
      linked_device_type: 'network_switch',
      linked_device_id: equipment.id
    };
    setDevices(updatedDevices);
  };

  const handlePhysicalServerSelect = (server: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: server.server_name || `${server.brand} ${server.model}`,
      device_category: 'sunucu',
      inventory_number: '',
      serial_number: server.serial_number || '',
      linked_device_type: 'physical_server',
      linked_device_id: server.id
    };
    setDevices(updatedDevices);
  };

  const handleCameraSelect = (camera: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: camera.alias || `${camera.brand} ${camera.model}`,
      device_category: 'kamera',
      inventory_number: camera.inventory_number || '',
      serial_number: '',
      linked_device_type: 'camera',
      linked_device_id: camera.id
    };
    setDevices(updatedDevices);
  };

  const handleIotDeviceSelect = (device: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: device.device_name || `${device.brand} ${device.model}`,
      device_category: 'iot-ekran',
      inventory_number: '',
      serial_number: device.serial_number || '',
      linked_device_type: 'iot_device',
      linked_device_id: device.id
    };
    setDevices(updatedDevices);
  };

  const searchMobilePhones = (query: string, deviceIndex: number) => {
    if (!query || query.length < 2) {
      setMobilePhoneSearchResults(prev => ({ ...prev, [deviceIndex]: [] }));
      return;
    }

    const filtered = mobilePhonesData.filter(phone => {
      if (!serviceData.location_id || phone.location_id !== serviceData.location_id) return false;
      const searchLower = String(query || '').toLowerCase();
      return (
        String(phone.inventory_number || '').toLowerCase().includes(searchLower) ||
        String(phone.imei1 || '').toLowerCase().includes(searchLower) ||
        (phone.imei2 && String(phone.imei2 || '').toLowerCase().includes(searchLower)) ||
        (phone.assigned_user_name && String(phone.assigned_user_name || '').toLowerCase().includes(searchLower)) ||
        String(phone.brand || '').toLowerCase().includes(searchLower) ||
        String(phone.model || '').toLowerCase().includes(searchLower) ||
        (phone.gsm_number && phone.gsm_number.includes(searchLower))
      );
    });

    setMobilePhoneSearchResults(prev => ({ ...prev, [deviceIndex]: filtered }));
  };

  const handleMobilePhoneSelect = (mobilePhone: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: `${mobilePhone.brand} ${mobilePhone.model}`,
      device_category: 'telefon',
      inventory_number: mobilePhone.inventory_number,
      serial_number: mobilePhone.imei1,
      mobile_phone_id: mobilePhone.id,
      linked_device_type: 'mobile_phone',
      linked_device_id: mobilePhone.id
    };
    setDevices(updatedDevices);
    setMobilePhoneSearchResults(prev => ({ ...prev, [deviceIndex]: [] }));
    setMobilePhoneSearchTerm(prev => ({ ...prev, [deviceIndex]: '' }));
  };

  const handleHandheldTerminalSelect = (terminal: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: `${terminal.brand} ${terminal.model}`,
      device_category: 'el-terminali',
      inventory_number: terminal.inventory_number || '',
      serial_number: terminal.serial_number || '',
      linked_device_type: 'handheld_terminal',
      linked_device_id: terminal.id
    };
    setDevices(updatedDevices);
  };

  const searchComputers = (query: string, deviceIndex: number) => {
    if (!query || query.length < 2) {
      setComputerSearchResults(prev => ({ ...prev, [deviceIndex]: [] }));
      return;
    }

    const filtered = computersData.filter(computer => {
      if (!serviceData.location_id || computer.location_id !== serviceData.location_id) return false;
      const searchLower = String(query || '').toLowerCase();
      return (
        String(computer.inventory_number || '').toLowerCase().includes(searchLower) ||
        String(computer.serial_number || '').toLowerCase().includes(searchLower) ||
        (computer.assigned_user_name && String(computer.assigned_user_name || '').toLowerCase().includes(searchLower)) ||
        String(computer.brand || '').toLowerCase().includes(searchLower) ||
        String(computer.model || '').toLowerCase().includes(searchLower) ||
        `${computer.brand} ${computer.model}`.toLowerCase().includes(searchLower)
      );
    });

    setComputerSearchResults(prev => ({ ...prev, [deviceIndex]: filtered }));
  };

  const handleComputerSelect = (computer: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: `${computer.brand} ${computer.model}`,
      device_category: 'bilgisayar',
      inventory_number: computer.inventory_number || '',
      serial_number: computer.serial_number || '',
      computer_id: computer.id,
      linked_device_type: 'computer',
      linked_device_id: computer.id
    };
    setDevices(updatedDevices);
    setComputerSearchResults(prev => ({ ...prev, [deviceIndex]: [] }));
    setComputerSearchTerm(prev => ({ ...prev, [deviceIndex]: '' }));
  };

  const searchBySerial = (query: string, deviceIndex: number) => {
    if (!query || query.length < 2) {
      setSerialSuggestions(prev => ({ ...prev, [deviceIndex]: [] }));
      return;
    }
    const q = query.toLowerCase();
    const results: any[] = [];

    mobilePhonesData.forEach(p => {
      if (String(p.imei1 || '').toLowerCase().includes(q) || String(p.imei2 || '').toLowerCase().includes(q) || String(p.inventory_number || '').toLowerCase().includes(q)) {
        results.push({ _type: 'mobile_phone', _label: `${p.brand} ${p.model}`, _sub: `IMEI: ${p.imei1}${p.inventory_number ? ' · Env: ' + p.inventory_number : ''}${p.assigned_user_name ? ' · ' + p.assigned_user_name : ''}`, id: p.id, brand: p.brand, model: p.model, serial_number: p.imei1, inventory_number: p.inventory_number || '', device_category: 'telefon', mobile_phone_id: p.id });
      }
    });
    computersData.forEach(c => {
      if (String(c.serial_number || '').toLowerCase().includes(q) || String(c.inventory_number || '').toLowerCase().includes(q)) {
        results.push({ _type: 'computer', _label: `${c.brand} ${c.model}`, _sub: `SN: ${c.serial_number}${c.inventory_number ? ' · Env: ' + c.inventory_number : ''}${c.assigned_user_name ? ' · ' + c.assigned_user_name : ''}`, id: c.id, brand: c.brand, model: c.model, serial_number: c.serial_number || '', inventory_number: c.inventory_number || '', device_category: 'bilgisayar', computer_id: c.id });
      }
    });
    radiosData.forEach(r => {
      if (String(r.serial_number || '').toLowerCase().includes(q) || String(r.inventory_number || '').toLowerCase().includes(q)) {
        results.push({ _type: 'radio', _label: `${r.brand} ${r.model}`, _sub: `SN: ${r.serial_number}${r.inventory_number ? ' · Env: ' + r.inventory_number : ''}${r.assigned_user_name ? ' · ' + r.assigned_user_name : ''}`, id: r.id, brand: r.brand, model: r.model, serial_number: r.serial_number || '', inventory_number: r.inventory_number || '', device_category: 'telsiz' });
      }
    });
    scalesData.forEach(s => {
      if (String(s.serial_number || '').toLowerCase().includes(q)) {
        results.push({ _type: 'scale', _label: `${s.brand} ${s.model}`, _sub: `SN: ${s.serial_number}${s.location_name ? ' · ' + s.location_name : ''}`, id: s.id, brand: s.brand, model: s.model, serial_number: s.serial_number || '', inventory_number: '', device_category: 'terazi' });
      }
    });
    printersData.forEach(p => {
      if (String(p.serial_number || '').toLowerCase().includes(q)) {
        results.push({ _type: 'printer', _label: `${p.brand} ${p.model}`, _sub: `SN: ${p.serial_number}${p.location ? ' · ' + p.location : ''}`, id: p.id, brand: p.brand, model: p.model, serial_number: p.serial_number || '', inventory_number: '', device_category: 'yazici' });
      }
    });
    handheldTerminalsData.forEach(t => {
      if (String(t.serial_number || '').toLowerCase().includes(q) || String(t.inventory_number || '').toLowerCase().includes(q)) {
        results.push({ _type: 'handheld', _label: `${t.brand} ${t.model}`, _sub: `SN: ${t.serial_number || '-'}${t.inventory_number ? ' · Env: ' + t.inventory_number : ''}`, id: t.id, brand: t.brand, model: t.model, serial_number: t.serial_number || '', inventory_number: t.inventory_number || '', device_category: 'el-terminali' });
      }
    });

    setSerialSuggestions(prev => ({ ...prev, [deviceIndex]: results.slice(0, 8) }));
  };

  const handleSerialSuggestionSelect = (suggestion: any, deviceIndex: number) => {
    const updatedDevices = [...devices];
    updatedDevices[deviceIndex] = {
      ...updatedDevices[deviceIndex],
      device_name: `${suggestion.brand} ${suggestion.model}`,
      device_category: suggestion.device_category,
      serial_number: suggestion.serial_number,
      inventory_number: suggestion.inventory_number || '',
      mobile_phone_id: suggestion.mobile_phone_id || null,
      computer_id: suggestion.computer_id || null,
      linked_device_type: suggestion._type,
      linked_device_id: suggestion.id
    };
    setDevices(updatedDevices);
    setSerialSuggestions(prev => ({ ...prev, [deviceIndex]: [] }));
    if (suggestion._type === 'radio') {
      setRadioLookupStatus(prev => ({ ...prev, [deviceIndex]: 'found' }));
    }
  };

  const lookupRadioBySerial = async (serialNumber: string, deviceIndex: number) => {
    if (!serialNumber || serialNumber.length < 3) {
      setRadioLookupStatus(prev => ({ ...prev, [deviceIndex]: null }));
      return;
    }
    setRadioLookupStatus(prev => ({ ...prev, [deviceIndex]: 'loading' }));
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/radios/lookup/${encodeURIComponent(serialNumber)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const radio = await response.json();
        const updatedDevices = [...devices];
        updatedDevices[deviceIndex] = {
          ...updatedDevices[deviceIndex],
          device_name: `${radio.brand} ${radio.model}`,
          inventory_number: radio.inventory_number || '',
          linked_device_type: 'radio',
          linked_device_id: radio.id
        };
        setDevices(updatedDevices);
        setRadioLookupStatus(prev => ({ ...prev, [deviceIndex]: 'found' }));
      } else {
        setRadioLookupStatus(prev => ({ ...prev, [deviceIndex]: 'not-found' }));
      }
    } catch {
      setRadioLookupStatus(prev => ({ ...prev, [deviceIndex]: null }));
    }
  };

  const addDevice = () => {
    const newIndex = devices.length;
    setDevices([...devices, {
      device_category: '',
      device_name: '',
      inventory_number: '',
      serial_number: '',
      fault_description: '',
      mobile_phone_id: null as number | null,
      computer_id: null as number | null,
      linked_device_type: null as string | null,
      linked_device_id: null as number | null
    }]);
    // Yeni cihazı açık olarak başlat, diğerlerini kapat
    const newExpanded = { [newIndex]: true };
    setExpandedDevices(newExpanded);
  };
  
  const removeDevice = (index: number) => {
    if (devices.length > 1) {
      setDevices(devices.filter((_, i) => i !== index));
    }
  };
  
  const updateDevice = (index: number, field: string, value: string) => {
    const updatedDevices = [...devices];
    updatedDevices[index] = { ...updatedDevices[index], [field]: value };
    setDevices(updatedDevices);
  };
  
  const toggleDeviceExpanded = (index: number) => {
    setExpandedDevices(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const copyDevice = (index: number) => {
    const deviceToCopy = devices[index];
    const newDevice = {
      ...deviceToCopy,
      inventory_number: '',
      serial_number: '',
      fault_description: ''
    };
    const newIndex = devices.length;
    setDevices([...devices, newDevice]);
    // Yeni cihazı açık olarak başlat
    setExpandedDevices(prev => ({ ...prev, [newIndex]: true }));
  };
  
  const checkDeviceDuplication = (index: number, field: 'inventory_number' | 'serial_number', value: string) => {
    if (!value.trim()) return false;
    
    // Envanter no için özel durumlar: 'yok', '0' gibi değerler muaf
    if (field === 'inventory_number') {
      const normalizedValue = value.trim().toLowerCase();
      if (normalizedValue === 'yok' || normalizedValue === '0' || normalizedValue === '') {
        return false;
      }
    }
    
    // Sadece seri no için çakışma kontrolü yap
    if (field === 'serial_number') {
      return devices.some((device, i) => {
        if (i === index) return false; // Aynı cihazı kontrol etme
        if (!device[field]) return false;
        return device[field].trim() === value.trim();
      });
    }
    
    // Envanter no için kontrol (opsiyonel)
    if (field === 'inventory_number') {
      return devices.some((device, i) => {
        if (i === index) return false;
        if (!device[field]) return false;
        const deviceValue = device[field].trim().toLowerCase();
        const checkValue = value.trim().toLowerCase();
        if (deviceValue === 'yok' || deviceValue === '0' || deviceValue === '') return false;
        return deviceValue === checkValue;
      });
    }
    
    return false;
  };

  const handleSave = async () => {
    if (!serviceData.service_number || !serviceData.company_id || !serviceData.location_id) {
      showGlobalNotification.error(t('common.error'), t('techservice.notifRequiredFields'));
      return;
    }

    // Cihaz validasyonu
    const validDevices = devices.filter(device => device.device_name.trim() !== '');
    if (validDevices.length === 0) {
      showGlobalNotification.error(t('common.error'), t('techservice.notifDeviceRequired'));
      return;
    }
    
    setSaving(true);
    setSaveProgress(t('techservice.progressStarting'));
    
    try {
      if (editingService) {
        // Değişiklik kontrolü - hem serviceData hem de devices array'ini kontrol et
        const hasServiceDataChanges = Object.keys(serviceData).some(key => {
          const currentValue = serviceData[key as keyof typeof serviceData];
          const originalValue = key === 'estimated_completion' 
            ? (editingService.estimated_completion ? editingService.estimated_completion.split('T')[0] : '')
            : editingService[key as keyof typeof editingService];
          
          // Boş değerler için normalize et
          const normalizedCurrent = currentValue === '' ? null : currentValue;
          const normalizedOriginal = originalValue === '' ? null : originalValue;
          
          return normalizedCurrent !== normalizedOriginal;
        });
        
        // Cihaz bilgilerindeki değişiklikleri kontrol et
        const hasDeviceChanges = devices[0] && (
          devices[0].device_name !== editingService.device_name ||
          (devices[0].inventory_number || '') !== (editingService.inventory_number || '') ||
          (devices[0].serial_number || '') !== (editingService.serial_number || '') ||
          (devices[0].device_category || '') !== (editingService.device_category || '') ||
          (devices[0].fault_description || '') !== (editingService.fault_description || '')
        );
        
        const hasChanges = hasServiceDataChanges || hasDeviceChanges;
        
        if (!hasChanges) {
          showGlobalNotification.warning(t('common.warning'), t('techservice.notifNoChanges'));
          setShowModal(false);
          setSaving(false);
          setSaveProgress('');
          return;
        }
        
        setSaveProgress(t('techservice.progressCheckingChanges'));
        
        // Düzenleme modunda tek cihaz güncelle (eski sistem uyumlu)
        const deviceServiceData = {
          ...serviceData,
          device_category: devices[0].device_category,
          device_name: devices[0].device_name,
          inventory_number: devices[0].inventory_number,
          serial_number: devices[0].serial_number,
          fault_description: devices[0].fault_description || serviceData.fault_description,
          // E-posta seçeneklerini ekle
          sendEmailToContact: formData.sendEmailToContact,
          sendEmailToTechnician: formData.sendEmailToTechnician,
          sendEmailToSupplier: formData.sendEmailToSupplier
        };

        setSaveProgress(t('techservice.progressUpdating'));
        await apiService.updateTechnicalService(editingService.id, deviceServiceData);
        setSaveProgress(t('techservice.progressUpdateDone'));
        showGlobalNotification.success(t('common.success'), t('techservice.notifUpdateSuccess'));
      } else {
        setSaveProgress(t('techservice.progressCreating'));
        
        // Çoklu cihaz desteği - tek servis kaydında birden fazla cihaz
        // Backend'in kabul ettiği alanları içeren temiz device objeleri oluştur
        const cleanedDevices = validDevices.map(device => ({
          device_name: device.device_name || '',
          device_category: device.device_category || '',
          inventory_number: device.inventory_number || '',
          serial_number: device.serial_number || '',
          fault_description: device.fault_description || '',
          mobile_phone_id: device.mobile_phone_id || null,
          computer_id: device.computer_id || null,
          linked_device_type: device.linked_device_type || null,
          linked_device_id: device.linked_device_id || null
        }));
        
        const multiDeviceServiceData = {
          ...serviceData,
          devices: cleanedDevices,
          // E-posta seçeneklerini ekle
          sendEmailToContact: formData.sendEmailToContact,
          sendEmailToTechnician: formData.sendEmailToTechnician,
          sendEmailToSupplier: formData.sendEmailToSupplier
        };
        
        setSaveProgress(t('techservice.progressSaving'));
        await apiService.createTechnicalService(multiDeviceServiceData);
        setSaveProgress(t('techservice.progressSaved'));
        showGlobalNotification.success(t('common.success'), language === 'en'
          ? `Technical service with ${validDevices.length} device(s) created`
          : `${validDevices.length} cihaz içeren teknik servis oluşturuldu`);
      }
      
      // Reload data
      setSaveProgress(t('techservice.progressRefreshing'));
      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving technical service:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('techservice.notifSaveError'));
    } finally {
      setSaving(false);
      setSaveProgress('');
    }
  };

  const handleDelete = async (id: number) => {
    const serviceToDelete = servicesData.find(s => s.id === id);
    if (!serviceToDelete) return;
    
    if (confirm(t('techservice.confirmDelete'))) {
      try {
        await apiService.deleteTechnicalService(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('techservice.notifDeleteSuccess'));
      } catch (error: any) {
        console.error('Error deleting technical service:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('techservice.notifDeleteError'));
      }
    }
  };

  const handleRejectQuote = async () => {
    if (!rejectModalService || !rejectionReason.trim()) {
      showGlobalNotification.error(t('common.error'), t('techservice.notifRejectRequired'));
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/technical-service/${rejectModalService.id}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejection_reason: rejectionReason.trim()
        })
      });

      if (response.ok) {
        showGlobalNotification.success(t('common.success'), t('techservice.notifRejectSuccess'));
        setRejectModalService(null);
        setRejectionReason('');
        await loadData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('common.error'), error.error || t('techservice.notifRejectError'));
      }
    } catch (error) {
      showGlobalNotification.error(t('common.error'), t('techservice.notifRejectError'));
    }
  };

  const showServiceDetail = (service: TechnicalService) => {
    setSelectedService(service);
    setShowDetailModal(true);
  };

  const showServiceHistory = (service: TechnicalService) => {
    setSelectedService(service);
    setShowHistoryModal(true);
  };
  
  const handleViewSupplierHistory = async (service: TechnicalService) => {
    setSelectedServiceForHistory(service);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/supplier-history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSupplierHistory(data);
        setShowSupplierHistoryModal(true);
      }
    } catch (error) {
      console.error('Error loading supplier history:', error);
      showGlobalNotification.error(t('common.error'), t('techservice.notifSupplierHistoryError'));
    }
  };

  const handleViewServiceHistory = async (service: TechnicalService) => {
    setSelectedServiceForHistory(service);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/technical-service/${service.id}/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServiceHistory(data);
        setShowServiceHistoryModal(true);
      }
    } catch (error) {
      console.error('Error loading service history:', error);
      showGlobalNotification.error(t('common.error'), t('techservice.notifServiceHistoryError'));
    }
  };

  const printCargoLabel = (service: TechnicalService) => {
    if (!service.supplier_id) {
      showGlobalNotification.error(t('common.error'), t('techservice.notifCargoNoSupplier'));
      return;
    }
    
    const token = localStorage.getItem('auth_token');
    const params = new URLSearchParams({
      token: token || '',
      width: labelSettings.width.toString(),
      height: labelSettings.height.toString(),
      orientation: labelSettings.orientation,
      fontSize: labelSettings.fontSize.toString(),
      margin: labelSettings.margin.toString()
    });
    const url = `${import.meta.env.VITE_API_URL}/technical-service/${service.id}/cargo-label?${params}`;
    window.open(url, '_blank');
  };
  
  const openLabelSettings = () => {
    setShowLabelSettingsModal(true);
  };

  const downloadQuoteFile = (fileId: number, fileName: string) => {
    const token = localStorage.getItem('auth_token');
    const url = `${import.meta.env.VITE_API_URL}/technical-service/quote-file/${fileId}/download?token=${token}`;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'yeni':
        return Clock;
      case 'devam-ediyor':
        return Wrench;
      case 'beklemede':
        return AlertCircle;
      case 'tamamlandi':
        return CheckCircle;
      case 'iptal-edildi':
        return XCircle;
      default:
        return Clock;
    }
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'TRY': return '₺';
      default: return '₺';
    }
  };

  const getServiceHistory = (serviceId: number) => {
    // In real app, this would be an API call
    return [];
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  const printServiceForm = (service: TechnicalService) => {
    const token = localStorage.getItem('auth_token');
    const url = `${import.meta.env.VITE_API_URL}/technical-service/${service.id}/pdf?token=${token}`;
    window.open(url, '_blank');
  };

  const LABEL_STATUS_META: {[key: string]: { bg: string; fg: string }} = {
    'servis-kaydi-olusturuldu': { bg: '#ede9fe', fg: '#6d28d9' },
    'servise-gonderildi': { bg: '#f3e8ff', fg: '#7e22ce' },
    'isleme-alindi': { bg: '#fef9c3', fg: '#854d0e' },
    'teklif-yuklendi': { bg: '#ffedd5', fg: '#9a3412' },
    'siparis-olusturuldu': { bg: '#e0e7ff', fg: '#3730a3' },
    'onarildi-gonderildi': { bg: '#cffafe', fg: '#155e75' },
    'tamamlandi': { bg: '#dcfce7', fg: '#166534' },
    'iptal-edildi': { bg: '#fee2e2', fg: '#991b1b' },
    'reddedildi': { bg: '#fecaca', fg: '#7f1d1d' }
  };

  const LABEL_PRIORITY_META: {[key: string]: { bg: string; fg: string }} = {
    'dusuk': { bg: '#f1f5f9', fg: '#475569' },
    'normal': { bg: '#ede9fe', fg: '#6d28d9' },
    'yuksek': { bg: '#ffedd5', fg: '#9a3412' },
    'acil': { bg: '#fee2e2', fg: '#b91c1c' }
  };

  const escapeHtml = (value: any): string => {
    if (value === null || value === undefined || value === '') return '';
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };

  const printServiceLabel = (service: any) => {
    const locale = language === 'en' ? 'en-US' : 'tr-TR';
    const statusMeta = LABEL_STATUS_META[service.status] || { bg: '#f1f5f9', fg: '#475569' };
    const priorityMeta = LABEL_PRIORITY_META[service.priority] || { bg: '#f1f5f9', fg: '#475569' };
    const statusLabel = escapeHtml(getStatusLabel(service.status)) || '-';
    const priorityLabel = escapeHtml(getPriorityLabel(service.priority)) || '-';
    const companyName = escapeHtml(service.company?.name) || '-';
    const locationName = escapeHtml(service.location?.name) || '-';
    const deviceName = escapeHtml(service.device_name) || '-';
    const extraDeviceCount = service.device_count > 1 ? service.device_count - 1 : 0;

    const labelContent = `
      <div style="font-family: 'Segoe UI', Arial, sans-serif; width: 300px; border: 1px solid #cbd5e1; border-radius: 10px; overflow: hidden; color: #1e293b;">
        <div style="background: #6d28d9; color: #fff; padding: 10px 14px; text-align: center;">
          <div style="font-size: 9px; letter-spacing: .08em; text-transform: uppercase; opacity: .85;">${escapeHtml(t('techservice.printLabelTitle'))}</div>
          <div style="font-size: 19px; font-weight: 800; font-family: 'Courier New', monospace; letter-spacing: .04em; margin-top: 2px;">${escapeHtml(service.service_number)}</div>
        </div>
        <div style="padding: 12px 14px;">
          <div style="display: flex; gap: 6px; margin-bottom: 10px;">
            <span style="background:${statusMeta.bg}; color:${statusMeta.fg}; font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 999px;">${statusLabel}</span>
            <span style="background:${priorityMeta.bg}; color:${priorityMeta.fg}; font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 999px;">${priorityLabel}</span>
          </div>

          <div style="font-size: 14px; font-weight: 700; margin-bottom: 2px;">${deviceName}${extraDeviceCount > 0 ? ` <span style="font-size:11px; font-weight:600; color:#7c3aed;">(+${extraDeviceCount} ${escapeHtml(t('techservice.colDevice')).toLowerCase()})</span>` : ''}</div>
          ${service.device_category ? `<div style="font-size: 10.5px; color: #7c3aed; font-weight: 600; text-transform: capitalize; margin-bottom: 8px;">${escapeHtml(service.device_category)}</div>` : '<div style="margin-bottom: 8px;"></div>'}

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px 10px; font-size: 11px; margin-bottom: 10px;">
            ${service.inventory_number ? `<div><span style="display:block; color:#94a3b8; font-size:9.5px; text-transform:uppercase;">${escapeHtml(t('techservice.printLabelInventory'))}</span><strong>${escapeHtml(service.inventory_number)}</strong></div>` : ''}
            ${service.serial_number ? `<div><span style="display:block; color:#94a3b8; font-size:9.5px; text-transform:uppercase;">${escapeHtml(t('techservice.printLabelSerial'))}</span><strong>${escapeHtml(service.serial_number)}</strong></div>` : ''}
          </div>

          <div style="border-top: 1px dashed #e2e8f0; padding-top: 8px; font-size: 11px; line-height: 1.5;">
            <div><span style="color:#94a3b8;">${escapeHtml(t('techservice.printLabelCompany'))}</span> <strong>${companyName}</strong></div>
            <div><span style="color:#94a3b8;">${escapeHtml(t('techservice.printLabelLocation'))}</span> <strong>${locationName}</strong></div>
            <div><span style="color:#94a3b8;">${escapeHtml(t('techservice.printLabelDate'))}</span> <strong>${new Date(service.created_at).toLocaleDateString(locale)}</strong></div>
          </div>
        </div>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    printWindow?.document.write(`
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(t('techservice.printWindowTitle'))} - ${escapeHtml(service.service_number)}</title>
          <style>
            @page { margin: 8mm; }
            body { margin: 0; padding: 16px; display: flex; justify-content: center; }
          </style>
        </head>
        <body>${labelContent}</body>
      </html>
    `);
    printWindow?.document.close();
    printWindow?.focus();
    printWindow?.print();
  };



  const sendEmailNotification = (service: TechnicalService, type: 'created' | 'updated' | 'completed') => {
    // Here would be the email sending logic
  };

  const editLocked = (service: TechnicalService) =>
    service.status === 'teklif-yuklendi' || service.status === 'siparis-olusturuldu' || service.status === 'onarildi-gonderildi';

  // Satır işlem butonları: masaüstü tablo ve mobil kart görünümünde ortak
  const renderServiceActions = (service: TechnicalService, variant: 'table' | 'card') => {
    const btn = variant === 'card'
      ? 'min-h-[44px] min-w-[44px] rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700'
      : 'p-1 rounded flex items-center justify-center';
    const icon = variant === 'card' ? 'w-5 h-5' : 'w-4 h-4';
    return (
      <>
        <button
          onClick={() => showServiceDetail(service)}
          className={`${btn} text-gray-400 hover:text-primary transition-colors`}
          title={t('techservice.titleViewDetail')}
          aria-label={t('techservice.titleViewDetail')}
        >
          <Eye className={icon} />
        </button>
        <button
          onClick={() => handleViewServiceHistory(service)}
          className={`${btn} text-gray-400 hover:text-green-600 transition-colors`}
          title={t('techservice.titleServiceHistory')}
          aria-label={t('techservice.titleServiceHistory')}
        >
          <History className={icon} />
        </button>
        <button
          onClick={() => handleEdit(service)}
          disabled={editLocked(service)}
          className={`${btn} transition-colors ${
            editLocked(service)
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-400 hover:text-primary'
          }`}
          title={editLocked(service) ? t('techservice.titleEditDisabled') : t('techservice.titleEdit')}
          aria-label={editLocked(service) ? t('techservice.titleEditDisabled') : t('techservice.titleEdit')}
        >
          <Edit2 className={icon} />
        </button>
        <button
          onClick={() => handleDelete(service.id)}
          className={`${btn} ${variant === 'card' ? 'order-last ml-auto text-red-600' : 'text-gray-400'} hover:text-red-600 transition-colors`}
          title={t('common.delete')}
          aria-label={t('common.delete')}
        >
          <Trash2 className={icon} />
        </button>
        <button
          onClick={() => printServiceForm(service)}
          className={`${btn} text-gray-400 hover:text-primary transition-colors`}
          title={t('techservice.titlePrintForm')}
          aria-label={t('techservice.titlePrintForm')}
        >
          <Printer className={icon} />
        </button>
        {service.supplier_id && (
          <>
            <button
              onClick={() => printCargoLabel(service)}
              className={`${btn} text-gray-400 hover:text-orange-600 transition-colors`}
              title={t('techservice.titlePrintCargo')}
              aria-label={t('techservice.titlePrintCargo')}
            >
              <FileText className={icon} />
            </button>
            <button
              onClick={openLabelSettings}
              className={`${btn} text-gray-400 hover:text-primary transition-colors`}
              title={t('techservice.titleLabelSettings')}
              aria-label={t('techservice.titleLabelSettings')}
            >
              <svg className={icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </>
        )}
        {service.status === 'teklif-yuklendi' && (
          <>
            <button
              onClick={async () => {
                const orderNumber = prompt(t('techservice.promptOrderNumber'));
                if (orderNumber && orderNumber.trim()) {
                  try {
                    const response = await fetch(`${import.meta.env.VITE_API_URL}/technical-service/${service.id}/create-order`, {
                      method: 'PUT',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        order_number: orderNumber.trim()
                      })
                    });

                    if (response.ok) {
                      showGlobalNotification.success(t('common.success'), `${language === 'en' ? 'Order created:' : 'Sipariş oluşturuldu:'} ${orderNumber}`);
                      loadData();
                    } else {
                      const error = await response.json();
                      showGlobalNotification.error(t('common.error'), error.error || t('techservice.notifOrderError'));
                    }
                  } catch (error) {
                    showGlobalNotification.error(t('common.error'), t('techservice.notifOrderError'));
                  }
                }
              }}
              className={`${btn} text-orange-600 hover:text-orange-800 transition-colors animate-pulse`}
              title={t('techservice.titleCreateOrder')}
              aria-label={t('techservice.titleCreateOrder')}
            >
              <svg className={icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </button>
            <button
              onClick={() => setRejectModalService(service)}
              className={`${btn} text-red-600 hover:text-red-800 transition-colors`}
              title={t('techservice.titleRejectQuote')}
              aria-label={t('techservice.titleRejectQuote')}
            >
              <svg className={icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </>
        )}
        {service.supplier_id && supplierQuoteStatus[service.id] && (
          <button
            onClick={() => {
              const token = localStorage.getItem('auth_token');
              window.open(`${import.meta.env.VITE_API_URL}/technical-service/${service.id}/supplier-quote-pdf?token=${token}`, '_blank');
            }}
            className={`${btn} text-purple-600 hover:text-purple-800 transition-colors`}
            title={t('techservice.titleSupplierQuote')}
            aria-label={t('techservice.titleSupplierQuote')}
          >
            <DollarSign className={icon} />
          </button>
        )}
        {service.status === 'onarildi-gonderildi' && (
          <button
            onClick={async () => {
              if (confirm(t('techservice.confirmComplete'))) {
                try {
                  const response = await fetch(`${import.meta.env.VITE_API_URL}/technical-service/${service.id}/complete`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                      'Content-Type': 'application/json'
                    }
                  });

                  if (response.ok) {
                    showGlobalNotification.success(t('common.success'), t('techservice.notifCompleteSuccess'));
                    loadData();
                  } else {
                    const error = await response.json();
                    showGlobalNotification.error(t('common.error'), error.error || t('techservice.notifCompleteError'));
                  }
                } catch (error) {
                  showGlobalNotification.error(t('common.error'), t('techservice.notifCompleteError'));
                }
              }
            }}
            className={`${btn} text-green-600 hover:text-green-800 transition-colors animate-pulse`}
            title={t('techservice.titleCompleteService')}
            aria-label={t('techservice.titleCompleteService')}
          >
            <CheckCircle className={icon} />
          </button>
        )}
      </>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('techservice.pageTitle')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('techservice.pageSubtitle')}</p>
        </div>
        <button
          onClick={handleAdd}
          className="w-full sm:w-auto flex items-center justify-center px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors mt-4 sm:mt-0"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('techservice.addNew')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 mb-6">
        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setFilterStatus('all')}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('techservice.statTotal')}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{servicesData.length}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg hidden sm:flex items-center justify-center">
              <Wrench className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setFilterStatus('isleme-alindi')}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('techservice.statProcessing')}</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-600">
                {servicesData.filter(s => s.status === 'isleme-alindi').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg hidden sm:flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setFilterStatus('tamamlandi')}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('techservice.statCompleted')}</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">
                {servicesData.filter(s => s.status === 'tamamlandi').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg hidden sm:flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setFilterStatus('teklif-yuklendi')}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('techservice.statQuotePending')}</p>
              <p className="text-xl sm:text-2xl font-bold text-orange-600">
                {servicesData.filter(s => s.status === 'teklif-yuklendi').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg hidden sm:flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setFilterStatus('reddedildi')}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('techservice.statRejected')}</p>
              <p className="text-xl sm:text-2xl font-bold text-red-600">
                {servicesData.filter(s => s.status === 'reddedildi').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg hidden sm:flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </button>

        {/* Uyarı Kartları */}
        <div className="bg-red-50 border border-red-200 rounded-lg shadow-sm p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-red-600 mb-1">{t('techservice.statOverdue')}</p>
              <p className="text-xl sm:text-2xl font-bold text-red-700">
                {servicesData.filter(s => {
                  const createdDate = new Date(s.created_at);
                  const twoWeeksAgo = new Date();
                  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
                  return s.status === 'servis-kaydi-olusturuldu' && createdDate < twoWeeksAgo;
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg hidden sm:flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-xs text-red-600">{t('techservice.statOverdueNote')}</p>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg shadow-sm p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-orange-600 mb-1">{t('techservice.statQuoteWaiting')}</p>
              <p className="text-xl sm:text-2xl font-bold text-orange-700">
                {servicesData.filter(s => {
                  const sentDate = new Date(s.updated_at || s.created_at);
                  const oneWeekAgo = new Date();
                  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                  return s.status === 'servise-gonderildi' && sentDate < oneWeekAgo;
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg hidden sm:flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-xs text-orange-600">{t('techservice.statQuoteWaitingNote')}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('techservice.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full"
          />
        </div>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">{t('techservice.filterAllStatuses')}</option>
          {filterStatusOptions.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>

        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">{t('techservice.filterAllPriorities')}</option>
          {priorityOptions.map(priority => (
            <option key={priority.value} value={priority.value}>{priority.label}</option>
          ))}
        </select>
      </div>

      {/* Services Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('techservice.colService')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('techservice.colCompanyLocation')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('techservice.colDevice')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('techservice.colStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('techservice.colPriority')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('techservice.colDate')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('techservice.colActions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredServices.map((service) => {
                const StatusIcon = getStatusIcon(service.status);
                return (
                  <tr key={service.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                          <Wrench className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{service.service_number}</div>
                          {service.inventory_number && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('techservice.envLabel')} {service.inventory_number}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{service.company?.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{service.location?.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {service.device_name}
                        {service.device_count > 1 && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary-dark">
                            +{service.device_count - 1} {t('techservice.colDevice').toLowerCase()}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">{service.fault_description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {allStatusOptions.find(s => s.value === service.status)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getPriorityColor(service.priority)}`}>
                        {priorityOptions.find(p => p.value === service.priority)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(service.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        {renderServiceActions(service, 'table')}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredServices.length === 0 && (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 dark:text-gray-400">{t('techservice.noRecords')}</p>
            </div>
          )}
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden">
          {filteredServices.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 dark:text-gray-400">{t('techservice.noRecords')}</p>
            </div>
          ) : (
            <div className="space-y-3 p-3 sm:p-4">
              {filteredServices.map((service) => {
                const StatusIcon = getStatusIcon(service.status);
                return (
                  <div key={service.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <div className="flex items-start flex-1 min-w-0">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                          <Wrench className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{service.service_number}</div>
                          {service.inventory_number && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{t('techservice.envLabel')} {service.inventory_number}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {getStatusLabel(service.status)}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(service.priority)}`}>
                          {getPriorityLabel(service.priority)}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{t('techservice.cardCompany')}</span>
                        <span className="text-gray-900 dark:text-gray-100 text-right flex-1 ml-2 truncate">{service.company?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{t('techservice.cardLocation')}</span>
                        <span className="text-gray-900 dark:text-gray-100 text-right flex-1 ml-2 truncate">{service.location?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{t('techservice.cardDevice')}</span>
                        <span className="text-gray-900 dark:text-gray-100 text-right flex-1 ml-2 truncate">{service.device_name}
                          {service.device_count > 1 && (
                            <span className="ml-1 inline-block">(+{service.device_count - 1})</span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{t('techservice.cardFault')}</span>
                        <span className="text-gray-900 dark:text-gray-100 text-right flex-1 ml-2 line-clamp-2">{service.fault_description}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{t('techservice.cardDate')}</span>
                        <span className="text-gray-900 dark:text-gray-100">{new Date(service.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1 mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                      {renderServiceActions(service, 'card')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Service Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingService ? t('techservice.modalEditTitle') : t('techservice.modalAddTitle')}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  className="-mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50"
                  aria-label={t('common.close')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Progress Bar */}
              {saving && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span>{saveProgress}</span>
                    <span>{t('techservice.savingProgress')}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full animate-pulse" style={{width: '100%'}}></div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldServiceNo')}
                  </label>
                  <input
                    type="text"
                    value={serviceData.service_number}
                    onChange={(e) => setServiceData({...serviceData, service_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="TS-2024-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldCompany')}
                  </label>
                  <select
                    value={serviceData.company_id}
                    onChange={(e) => setServiceData({...serviceData, company_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={0}>{t('techservice.selectCompany')}</option>
                    {companiesData.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldLocation')}
                  </label>
                  <select
                    value={serviceData.location_id}
                    onChange={(e) => setServiceData({...serviceData, location_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={0}>{t('techservice.selectLocation')}</option>
                    {Array.isArray(locationsData) && locationsData.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Çoklu Cihaz Bölümü */}
                <div className="col-span-1 md:col-span-2">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2 sm:gap-0">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('techservice.devicesSection')}</h3>
                    <button
                      type="button"
                      onClick={addDevice}
                      className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {t('techservice.addDevice')}
                    </button>
                  </div>
                  
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {devices.map((device, index) => {
                      const isExpanded = expandedDevices[index] || false;
                      const deviceTitle = device.device_name || `${t('techservice.deviceDefault')} ${index + 1}`;
                      
                      return (
                      <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div 
                          className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                          onClick={() => toggleDeviceExpanded(index)}
                        >
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">{deviceTitle}</h4>
                            {device.inventory_number && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 bg-primary/20 px-2 py-1 rounded">
                                {t('techservice.envLabel')} {device.inventory_number}
                              </span>
                            )}
                            {device.serial_number && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 bg-green-100 px-2 py-1 rounded">
                                {t('techservice.seriShort')} {device.serial_number}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyDevice(index);
                              }}
                              className="text-primary hover:text-primary-dark p-1"
                              title={t('techservice.titleCopyDevice')}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            {devices.length > 1 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeDevice(index);
                                }}
                                className="text-red-600 hover:text-red-800 p-1"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                              <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        {isExpanded && (
                        <div className="px-4 pb-4">
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('techservice.fieldCategory')}
                            </label>
                            <select
                              value={device.device_category}
                              onChange={(e) => updateDevice(index, 'device_category', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                            >
                              <option value="">{t('techservice.selectCategory')}</option>
                              <option value="bilgisayar">{t('techservice.catComputer')}</option>
                              <option value="yazici">{t('techservice.catPrinter')}</option>
                              <option value="telefon">{t('techservice.catPhone')}</option>
                              <option value="ag-ekipmani">{t('techservice.catNetwork')}</option>
                              <option value="sunucu">{t('techservice.catServer')}</option>
                              <option value="kamera">{t('techservice.catCamera')}</option>
                              <option value="projektor">{t('techservice.catProjector')}</option>
                              <option value="tablet">{t('techservice.catTablet')}</option>
                              <option value="ups">{t('techservice.catUps')}</option>
                              <option value="terazi">{t('techservice.catScale')}</option>
                              <option value="tv">{t('techservice.catTV')}</option>
                              <option value="telsiz">{t('techservice.catRadio')}</option>
                              <option value="iot-ekran">{t('techservice.catIotScreen')}</option>
                              <option value="el-terminali">{t('techservice.catHandheld')}</option>
                              <option value="diger">{t('techservice.catOther')}</option>
                            </select>

                            {/* Bilgisayar Seçimi */}
                            {device.device_category === 'bilgisayar' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSearchComputer')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <div className="relative">
                                  <input
                                    type="text"
                                    placeholder={t('techservice.placeholderSearchComputer')}
                                    value={computerSearchTerm[index] || ''}
                                    onChange={(e) => {
                                      const newTerm = e.target.value;
                                      setComputerSearchTerm(prev => ({ ...prev, [index]: newTerm }));
                                      searchComputers(newTerm, index);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                  />

                                  {/* Arama Sonuçları */}
                                  {computerSearchResults[index] && computerSearchResults[index].length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                      {computerSearchResults[index].map((computer: any) => (
                                        <div
                                          key={computer.id}
                                          onClick={() => handleComputerSelect(computer, index)}
                                          className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer border-b border-gray-100 last:border-b-0"
                                        >
                                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                            {computer.brand} {computer.model}
                                          </div>
                                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            <span className="inline-block mr-3">Envanter: {computer.inventory_number}</span>
                                            <span className="inline-block mr-3">SN: {computer.serial_number}</span>
                                          </div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {computer.assigned_user_name && (
                                              <span className="inline-block mr-3">Kullanıcı: {computer.assigned_user_name}</span>
                                            )}
                                            {computer.location_name && (
                                              <span className="inline-block">Lokasyon: {computer.location_name}</span>
                                            )}
                                          </div>
                                          {(computer.processor || computer.ram) && (
                                            <div className="text-xs text-gray-400 mt-1">
                                              {computer.processor && <span className="inline-block mr-2">{computer.processor}</span>}
                                              {computer.ram && <span className="inline-block">{computer.ram} RAM</span>}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                )}
                              </div>
                            )}

                            {/* Yazıcı Seçimi */}
                            {device.device_category === 'yazici' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSelectPrinter')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const selectedPrinter = printersData.find(p => p.id === parseInt(e.target.value));
                                      if (selectedPrinter) {
                                        handlePrinterSelect(selectedPrinter, index);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">{t('techservice.selectPrinterOption')}</option>
                                  {printersData.filter(printer => printer.location_id === serviceData.location_id).map(printer => (
                                    <option key={printer.id} value={printer.id}>
                                      {printer.name} - {printer.brand} {printer.model} ({printer.location})
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}

                            {/* Telefon Seçimi */}
                            {device.device_category === 'telefon' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSearchPhone')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <div className="relative">
                                  <input
                                    type="text"
                                    placeholder={t('techservice.placeholderSearchPhone')}
                                    value={mobilePhoneSearchTerm[index] || ''}
                                    onChange={(e) => {
                                      const newTerm = e.target.value;
                                      setMobilePhoneSearchTerm(prev => ({ ...prev, [index]: newTerm }));
                                      searchMobilePhones(newTerm, index);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                  />

                                  {/* Arama Sonuçları */}
                                  {mobilePhoneSearchResults[index] && mobilePhoneSearchResults[index].length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                      {mobilePhoneSearchResults[index].map((phone: any) => (
                                        <div
                                          key={phone.id}
                                          onClick={() => handleMobilePhoneSelect(phone, index)}
                                          className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer border-b border-gray-100 last:border-b-0"
                                        >
                                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                            {phone.brand} {phone.model}
                                          </div>
                                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            <span className="inline-block mr-3">Envanter: {phone.inventory_number}</span>
                                            <span className="inline-block mr-3">IMEI: {phone.imei1}</span>
                                            {phone.assigned_user_name && (
                                              <span className="inline-block">Kullanıcı: {phone.assigned_user_name}</span>
                                            )}
                                          </div>
                                          {phone.gsm_number && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                              GSM: {phone.gsm_number}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                )}
                              </div>
                            )}

                            {/* El Terminali Seçimi */}
                            {device.device_category === 'el-terminali' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSelectHandheld')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const selectedTerminal = handheldTerminalsData.find(terminal => terminal.id === parseInt(e.target.value));
                                      if (selectedTerminal) {
                                        handleHandheldTerminalSelect(selectedTerminal, index);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">{t('techservice.selectHandheldOption')}</option>
                                  {handheldTerminalsData.filter(terminal => terminal.location_id === serviceData.location_id).map(terminal => (
                                    <option key={terminal.id} value={terminal.id}>
                                      {terminal.brand} {terminal.model}
                                      {terminal.inventory_number ? ` - Env: ${terminal.inventory_number}` : ''}
                                      {terminal.serial_number ? ` - Seri: ${terminal.serial_number}` : ''}
                                      {terminal.location_name ? ` (${terminal.location_name})` : ''}
                                      {terminal.assigned_user_name ? ` - ${terminal.assigned_user_name}` : ''}
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}

                            {/* Telsiz Seçimi */}
                            {device.device_category === 'telsiz' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSelectRadio')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const selectedRadio = radiosData.find(r => r.id === parseInt(e.target.value));
                                      if (selectedRadio) {
                                        handleRadioSelect(selectedRadio, index);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">{t('techservice.selectRadioOption')}</option>
                                  {radiosData.filter(radio => radio.location_id === serviceData.location_id).map(radio => (
                                    <option key={radio.id} value={radio.id}>
                                      {radio.brand} {radio.model}
                                      {radio.inventory_number ? ` - Env: ${radio.inventory_number}` : ''}
                                      {radio.serial_number ? ` - Seri: ${radio.serial_number}` : ''}
                                      {radio.assigned_user_name ? ` - ${radio.assigned_user_name}` : ''}
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}

                            {/* Terazi Seçimi */}
                            {device.device_category === 'terazi' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSelectScale')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const selectedScale = scalesData.find(s => s.id === parseInt(e.target.value));
                                      if (selectedScale) {
                                        handleScaleSelect(selectedScale, index);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">{t('techservice.selectScaleOption')}</option>
                                  {scalesData.filter(scale => scale.location_id === serviceData.location_id).map(scale => (
                                    <option key={scale.id} value={scale.id}>
                                      {scale.brand} {scale.model}
                                      {scale.serial_number ? ` - Seri: ${scale.serial_number}` : ''}
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}

                            {/* Ağ Ekipmanı Seçimi */}
                            {device.device_category === 'ag-ekipmani' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSelectNetworkEquipment')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const selectedEquipment = networkEquipmentData.find(n => n.id === parseInt(e.target.value));
                                      if (selectedEquipment) {
                                        handleNetworkEquipmentSelect(selectedEquipment, index);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">{t('techservice.selectNetworkEquipmentOption')}</option>
                                  {networkEquipmentData.filter(eq => eq.location_id === serviceData.location_id).map(eq => (
                                    <option key={eq.id} value={eq.id}>
                                      {eq.switch_name || `${eq.brand} ${eq.model}`}
                                      {eq.inventory_number ? ` - Env: ${eq.inventory_number}` : ''}
                                      {eq.serial_number ? ` - Seri: ${eq.serial_number}` : ''}
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}

                            {/* Sunucu Seçimi */}
                            {device.device_category === 'sunucu' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSelectServer')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const selectedServer = physicalServersData.find(s => s.id === parseInt(e.target.value));
                                      if (selectedServer) {
                                        handlePhysicalServerSelect(selectedServer, index);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">{t('techservice.selectServerOption')}</option>
                                  {physicalServersData.filter(server => server.location_id === serviceData.location_id).map(server => (
                                    <option key={server.id} value={server.id}>
                                      {server.server_name || `${server.brand} ${server.model}`}
                                      {server.serial_number ? ` - Seri: ${server.serial_number}` : ''}
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}

                            {/* Kamera Seçimi */}
                            {device.device_category === 'kamera' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSelectCamera')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const selectedCamera = camerasData.find(c => c.id === parseInt(e.target.value));
                                      if (selectedCamera) {
                                        handleCameraSelect(selectedCamera, index);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">{t('techservice.selectCameraOption')}</option>
                                  {camerasData.filter(camera => camera.location_id === serviceData.location_id).map(camera => (
                                    <option key={camera.id} value={camera.id}>
                                      {camera.alias || `${camera.brand} ${camera.model}`}
                                      {camera.inventory_number ? ` - Env: ${camera.inventory_number}` : ''}
                                      {camera.nvr_name ? ` (${camera.nvr_name})` : ''}
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}

                            {/* IOT Ekran Seçimi */}
                            {device.device_category === 'iot-ekran' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('techservice.labelSelectIot')}
                                </label>
                                {!serviceData.location_id ? (
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('techservice.selectLocationFirst')}</p>
                                ) : (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const selectedDevice = iotDevicesData.find(d => d.id === parseInt(e.target.value));
                                      if (selectedDevice) {
                                        handleIotDeviceSelect(selectedDevice, index);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">{t('techservice.selectIotOption')}</option>
                                  {iotDevicesData.filter(dev => dev.location_id === serviceData.location_id).map(dev => (
                                    <option key={dev.id} value={dev.id}>
                                      {dev.device_name || `${dev.brand} ${dev.model}`}
                                      {dev.serial_number ? ` - Seri: ${dev.serial_number}` : ''}
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('techservice.fieldDeviceName')}
                            </label>
                            <input
                              type="text"
                              value={device.device_name}
                              onChange={(e) => updateDevice(index, 'device_name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                              placeholder="Dell OptiPlex 7090"
                            />
                          </div>
                          
                          <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('techservice.fieldInventoryNo')}
                            </label>
                            <input
                              type="text"
                              value={device.inventory_number}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (checkDeviceDuplication(index, 'inventory_number', value)) {
                                  showGlobalNotification.error(t('common.error'), t('techservice.notifDuplicateInventory'));
                                  return;
                                }
                                updateDevice(index, 'inventory_number', value);
                                searchDeviceHistory(value);
                              }}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm ${
                                device.inventory_number && checkDeviceDuplication(index, 'inventory_number', device.inventory_number) 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}
                              placeholder="INV-2024-001"
                            />
                            {deviceSuggestions.length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                {deviceSuggestions.map((suggestion, suggestionIndex) => (
                                  <div
                                    key={suggestionIndex}
                                    onClick={() => handleDeviceSuggestionSelect(suggestion, index)}
                                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer text-sm"
                                  >
                                    <div className="font-medium">{suggestion.device_name}</div>
                                    <div className="text-gray-500 dark:text-gray-400">
                                      {suggestion.inventory_number && `Env: ${suggestion.inventory_number}`}
                                      {suggestion.serial_number && ` | Seri: ${suggestion.serial_number}`}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('techservice.fieldSerialNo')}
                            </label>
                            <input
                              type="text"
                              value={device.serial_number}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (checkDeviceDuplication(index, 'serial_number', value)) {
                                  showGlobalNotification.error(t('common.error'), t('techservice.notifDuplicateSerial'));
                                  return;
                                }
                                updateDevice(index, 'serial_number', value);
                                searchDeviceHistory(value);
                                if (serialSearchTimerRef.current[index]) clearTimeout(serialSearchTimerRef.current[index]);
                                serialSearchTimerRef.current[index] = setTimeout(() => searchBySerial(value, index), 300);
                              }}
                              onBlur={() => setTimeout(() => setSerialSuggestions(prev => ({ ...prev, [index]: [] })), 200)}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm ${
                                device.serial_number && checkDeviceDuplication(index, 'serial_number', device.serial_number)
                                  ? 'border-red-300 bg-red-50'
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}
                              placeholder="DL7090-2024-001"
                            />
                            {serialSuggestions[index] && serialSuggestions[index].length > 0 && (
                              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                {serialSuggestions[index].map((s, si) => (
                                  <div
                                    key={si}
                                    onMouseDown={() => handleSerialSuggestionSelect(s, index)}
                                    className="px-3 py-2 hover:bg-purple-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 shrink-0">
                                        {s._type === 'mobile_phone' ? t('techservice.catPhone') : s._type === 'computer' ? t('techservice.catComputer') : s._type === 'radio' ? t('techservice.catRadio') : s._type === 'scale' ? t('techservice.catScale') : s._type === 'printer' ? t('techservice.catPrinter') : t('techservice.catHandheld')}
                                      </span>
                                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{s._label}</span>
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{s._sub}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {device.linked_device_type && !serialSuggestions[index]?.length && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-1">✓ {device.device_category === 'telsiz' ? t('techservice.catRadio') : device.device_category === 'telefon' ? t('techservice.catPhone') : device.device_category === 'bilgisayar' ? t('techservice.catComputer') : device.device_category === 'terazi' ? t('techservice.catScale') : device.device_category === 'yazici' ? t('techservice.catPrinter') : device.device_category === 'el-terminali' ? t('techservice.catHandheld') : t('techservice.deviceDefault')} {t('techservice.serialLinked')}.</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('techservice.fieldDeviceFault')}
                          </label>
                          <textarea
                            value={device.fault_description}
                            onChange={(e) => updateDevice(index, 'fault_description', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                            placeholder={t('techservice.placeholderDeviceFault')}
                          />
                        </div>
                        </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldContactPerson')}
                  </label>
                  <select
                    value={serviceData.contact_person_id}
                    onChange={(e) => setServiceData({...serviceData, contact_person_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={0}>{t('techservice.selectContactPerson')}</option>
                    {usersData.filter(u => u.location_id === serviceData.location_id).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldSupplier')}
                  </label>
                  <select
                    value={serviceData.supplier_id}
                    onChange={(e) => setServiceData({...serviceData, supplier_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={0}>{t('techservice.selectSupplier')}</option>
                    {suppliersData.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldTechnician')}
                  </label>
                  <select
                    value={serviceData.assigned_technician_id}
                    onChange={(e) => setServiceData({...serviceData, assigned_technician_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={0}>{t('techservice.selectTechnician')}</option>
                    {usersData.filter(user => user.role === 'admin' || user.role === 'superadmin' || user.role === 'teknisyen').map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldStatus')}
                  </label>
                  <select
                    value={serviceData.status}
                    onChange={(e) => setServiceData({...serviceData, status: e.target.value as TechnicalService['status']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldPriority')}
                  </label>
                  <select
                    value={serviceData.priority}
                    onChange={(e) => setServiceData({...serviceData, priority: e.target.value as TechnicalService['priority']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {priorityOptions.map((priority) => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.fieldEstimatedCompletion')}
                  </label>
                  <input
                    type="date"
                    value={serviceData.estimated_completion}
                    onChange={(e) => setServiceData({...serviceData, estimated_completion: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>


              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('techservice.fieldGeneralFault')}
                </label>
                <textarea
                  value={serviceData.fault_description}
                  onChange={(e) => setServiceData({...serviceData, fault_description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={t('techservice.placeholderGeneralFault')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('techservice.fieldNotes')}
                </label>
                <textarea
                  value={serviceData.notes}
                  onChange={(e) => setServiceData({...serviceData, notes: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={t('techservice.placeholderNotes')}
                />
              </div>
              
              {/* E-posta Gönderme Seçenekleri */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('techservice.emailSection')}</h3>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.sendEmailToContact || false}
                      onChange={(e) => setFormData({ ...formData, sendEmailToContact: e.target.checked })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {t('techservice.emailToContact')}
                      {serviceData.contact_person_id && serviceData.contact_person_id > 0 && (
                        <span className="text-gray-500 dark:text-gray-400 ml-1">
                          ({usersData.find(u => u.id === serviceData.contact_person_id)?.name})
                        </span>
                      )}
                    </span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.sendEmailToTechnician || false}
                      onChange={(e) => setFormData({ ...formData, sendEmailToTechnician: e.target.checked })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {t('techservice.emailToTechnician')}
                      {serviceData.assigned_technician_id && serviceData.assigned_technician_id > 0 && (
                        <span className="text-gray-500 dark:text-gray-400 ml-1">
                          ({usersData.find(u => u.id === serviceData.assigned_technician_id)?.name})
                        </span>
                      )}
                    </span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.sendEmailToSupplier || false}
                      onChange={(e) => setFormData({ ...formData, sendEmailToSupplier: e.target.checked })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {t('techservice.emailToSupplier')}
                      {serviceData.supplier_id && serviceData.supplier_id > 0 && (
                        <span className="text-gray-500 dark:text-gray-400 ml-1">
                          ({suppliersData.find(s => s.id === serviceData.supplier_id)?.name})
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-800 p-3 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                disabled={saving}
                className={`flex-1 sm:flex-none px-4 py-2 min-h-[44px] border rounded-md transition-colors ${
                  saving 
                    ? 'text-gray-400 border-gray-200 dark:border-gray-700 cursor-not-allowed' 
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                }`}
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex-1 sm:flex-none px-4 py-2 min-h-[44px] rounded-md transition-colors flex items-center justify-center space-x-2 ${
                  saving 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-purple-600 hover:bg-purple-700'
                } text-white`}
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>
                  {saving
                    ? (editingService ? t('techservice.btnSavingUpdate') : t('techservice.btnSavingCreate'))
                    : (editingService ? t('techservice.btnUpdate') : t('techservice.btnSave'))
                  }
                </span>
              </button>
            </div>
          </div>
        </Modal>

      {/* Service Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedService && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('techservice.detailTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedService.service_number}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 flex-shrink-0"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
              {/* Service Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.detailDeviceInfo')}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailDeviceLabel')}</span>
                        <span className="font-medium">{selectedService.device_name}</span>
                      </div>
                      {selectedService.inventory_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailInventoryLabel')}</span>
                          <span className="font-medium">{selectedService.inventory_number}</span>
                        </div>
                      )}
                      {selectedService.serial_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailSerialLabel')}</span>
                          <span className="font-medium">{selectedService.serial_number}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailStatusLabel')}</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedService.status)}`}>
                          {allStatusOptions.find(s => s.value === selectedService.status)?.label}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailPriorityLabel')}</span>
                        <span className={`font-medium ${getPriorityColor(selectedService.priority)}`}>
                          {priorityOptions.find(p => p.value === selectedService.priority)?.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.detailContactInfo')}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailCompanyLabel')}</span>
                        <span className="font-medium">{selectedService.company?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailLocationLabel')}</span>
                        <span className="font-medium">{selectedService.location?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailContactLabel')}</span>
                        <span className="font-medium">{selectedService.contact_person?.name}</span>
                      </div>
                      {selectedService.supplier && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailSupplierLabel')}</span>
                          <span className="font-medium">{selectedService.supplier.name}</span>
                        </div>
                      )}
                      {selectedService.assigned_technician && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailTechnicianLabel')}</span>
                          <span className="font-medium">{selectedService.assigned_technician.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fault Description */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.detailFaultTitle')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  {selectedService.fault_description}
                </p>
              </div>

              {/* Solution Description */}
              {selectedService.solution_description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.detailSolutionTitle')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-green-50 p-3 rounded-lg">
                    {selectedService.solution_description}
                  </p>
                </div>
              )}

              {/* Notes */}
              {selectedService.notes && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.detailNotesTitle')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedService.notes}
                  </p>
                </div>
              )}

              {/* Dates and Cost */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.detailDatesTitle')}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailCreatedDate')}</span>
                      <span className="font-medium">{new Date(selectedService.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                    </div>
                    {selectedService.estimated_completion && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailEstimatedDate')}</span>
                        <span className="font-medium">{new Date(selectedService.estimated_completion).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                      </div>
                    )}
                    {selectedService.actual_completion && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('techservice.detailActualDate')}</span>
                        <span className="font-medium text-green-600">{new Date(selectedService.actual_completion).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {selectedService.cost && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.detailCostTitle')}</h3>
                    <div className="text-2xl font-bold text-purple-600">
                      {getCurrencySymbol(selectedService.currency || 'TRY')}{(selectedService.cost || 0).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => printServiceForm(selectedService)}
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  {t('techservice.btnPrintForm')}
                </button>
                <button
                  onClick={() => printServiceLabel(selectedService)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('techservice.btnPrintLabel')}
                </button>
                {selectedService.supplier_id && (
                  <button
                    onClick={() => printCargoLabel(selectedService)}
                    className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {t('techservice.btnPrintCargo')}
                  </button>
                )}
                {selectedService.supplier_id && supplierQuoteStatus[selectedService.id] && (
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('auth_token');
                      window.open(`${import.meta.env.VITE_API_URL}/technical-service/${selectedService.id}/supplier-quote-pdf?token=${token}`, '_blank');
                    }}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg animate-pulse"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    {t('techservice.btnSupplierQuoteReady')}
                  </button>
                )}
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Service History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedService && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-start gap-2">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('techservice.historyTitle')}
                  </h2>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <p className="truncate">{selectedService.service_number} - {selectedService.device_name}</p>
                    <p className="mt-1 text-xs sm:text-sm">
                      <span className="font-medium">{t('techservice.historyCreatedDate')}</span> {new Date(selectedService.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')} •
                      <span className="font-medium">{t('techservice.historyStatusLabel')}</span>
                      <span className={`ml-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedService.status)}`}>
                        {allStatusOptions.find(s => s.value === selectedService.status)?.label}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 flex-shrink-0"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-3 sm:p-6">
              <div className="space-y-4">
                {getServiceHistory(selectedService.id).map((history) => (
                  <div
                    key={history.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <div className="w-3 h-3 bg-primary rounded-full mr-3" />
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {t('techservice.historyStatusChange')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <span className="font-medium">{history.old_status}</span> → <span className="font-medium text-green-600">{history.new_status}</span>
                        </p>
                        {history.notes && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {history.notes}
                          </p>
                        )}
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <span>{history.changed_by_user?.name}</span>
                          <span className="mx-2">•</span>
                          <span>{new Date(history.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Supplier History Modal */}
      <Modal isOpen={showSupplierHistoryModal} onClose={() => setShowSupplierHistoryModal(false)}>
          {selectedServiceForHistory && supplierHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('techservice.supplierHistoryTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                    {selectedServiceForHistory.service_number} - {selectedServiceForHistory.device_name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowSupplierHistoryModal(false);
                    setSelectedServiceForHistory(null);
                    setSupplierHistory(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 flex-shrink-0"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
              {/* Tedarikçi İşlemleri */}
              {supplierHistory.actions && supplierHistory.actions.length > 0 && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('techservice.supplierActionsTitle')}</h3>
                  <div className="space-y-3">
                    {supplierHistory.actions.map((action: any) => (
                      <div key={action.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-primary rounded-full mr-3" />
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {action.action_type === 'status_change' && t('techservice.supplierActStatusChange')}
                              {action.action_type === 'quote_upload' && t('techservice.supplierActQuoteUpload')}
                              {action.action_type === 'work_description' && t('techservice.supplierActWorkDesc')}
                              {action.action_type === 'file_upload' && t('techservice.supplierActFileUpload')}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(action.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </span>
                        </div>
                        
                        {action.old_status && action.new_status && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">{action.old_status}</span> → 
                            <span className="font-medium text-green-600 ml-1">{action.new_status}</span>
                          </p>
                        )}
                        
                        {action.quote_amount && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">{t('techservice.supplierQuoteAmount')}</span> {action.quote_amount} {action.quote_currency}
                          </p>
                        )}

                        {action.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{action.description}</p>
                        )}

                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-2">
                          <span>{action.supplier_user_name} ({action.supplier_name})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Work Details */}
              {supplierHistory.work_devices && supplierHistory.work_devices.length > 0 && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('techservice.workDetailsTitle')}</h3>
                  <div className="space-y-4">
                    {supplierHistory.work_devices.map((device: any) => (
                      <div key={device.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-green-50">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{device.device_name}</h4>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {device.work_date ? new Date(device.work_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : ''}
                          </span>
                        </div>
                        
                        {device.device_serial && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">{t('techservice.workSerialNo')}</span> {device.device_serial}
                          </p>
                        )}
                        
                        {device.fault_description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">{t('techservice.workFault')}</span> {device.fault_description}
                          </p>
                        )}
                        
                        {device.work_performed && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">{t('techservice.workPerformed')}</span> {device.work_performed}
                          </p>
                        )}
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mb-3">
                          {device.labor_hours > 0 && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">{t('techservice.workLaborHours')}</span> {device.labor_hours} {t('techservice.workHoursUnit')}
                            </p>
                          )}
                          {device.labor_cost > 0 && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">{t('techservice.workLaborCost')}</span> {device.labor_cost} {device.currency}
                            </p>
                          )}
                        </div>
                        
                        {/* Parçalar */}
                        {device.parts && device.parts.length > 0 && (
                          <div className="mt-3">
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.workParts')}</h5>
                            <div className="space-y-1">
                              {device.parts.map((part: any) => (
                                <div key={part.id} className="flex justify-between items-center text-sm bg-white dark:bg-gray-800 p-2 rounded">
                                  <span>{part.part_name} {part.part_code && `(${part.part_code})`}</span>
                                  <span>{part.quantity} x {part.unit_price} = {part.total_price} {part.currency}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('techservice.workDeviceTotal')} {device.total_device_cost} {device.currency}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {device.supplier_user_name} ({device.supplier_name})
                          </span>
                        </div>
                        
                        {device.notes && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
                            <span className="font-medium">{t('techservice.workNote')}</span> {device.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              

              
              {/* Hiç veri yoksa */}
              {(!supplierHistory.actions || supplierHistory.actions.length === 0) &&
               (!supplierHistory.work_devices || supplierHistory.work_devices.length === 0) && (
                <div className="text-center py-8">
                  <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 dark:text-gray-400">{t('techservice.noSupplierHistory')}</p>
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>

      {/* Service History Modal */}
      <Modal isOpen={showServiceHistoryModal} onClose={() => setShowServiceHistoryModal(false)}>
          {selectedServiceForHistory && serviceHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('techservice.serviceHistoryTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                    {selectedServiceForHistory.service_number} - {selectedServiceForHistory.device_name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowServiceHistoryModal(false);
                    setSelectedServiceForHistory(null);
                    setServiceHistory(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 flex-shrink-0"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
              {/* Sistem İşlemleri */}
              {serviceHistory.service_logs && serviceHistory.service_logs.length > 0 && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <Wrench className="w-5 h-5 mr-2 text-primary" />
                    {t('techservice.serviceLogsTitle')}
                  </h3>
                  <div className="space-y-3">
                    {serviceHistory.service_logs.map((log: any) => {
                      const isSupplier = log.log_type === 'supplier';
                      const actionType = log.action_type || log.action;
                      const isEmailNotification = actionType === 'email_notification' || actionType === 'email_notification_failed';
                      
                      // E-posta bildirimleri için özel stil
                      const bgColor = isEmailNotification 
                        ? (actionType === 'email_notification_failed' ? 'bg-red-50' : 'bg-green-50')
                        : (isSupplier ? 'bg-purple-50' : 'bg-primary/10');
                      const iconColor = isEmailNotification 
                        ? (actionType === 'email_notification_failed' ? 'bg-red-500' : 'bg-green-500')
                        : (isSupplier ? 'bg-purple-500' : 'bg-primary');
                      
                      return (
                      <div key={`${log.log_type || 'system'}-${log.id}`} className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${bgColor}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            <div className={`w-3 h-3 ${iconColor} rounded-full mr-3`} />
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {isEmailNotification ? (
                                <>
                                  {actionType === 'email_notification' && t('techservice.serviceActEmailSent')}
                                  {actionType === 'email_notification_failed' && t('techservice.serviceActEmailFailed')}
                                </>
                              ) : isSupplier ? (
                                <>
                                  {actionType === 'status_change' && t('techservice.serviceActSupplierStatus')}
                                  {actionType === 'quote_upload' && t('techservice.serviceActSupplierQuote')}
                                  {actionType === 'quote_update' && t('techservice.serviceActSupplierQuoteUpdate')}
                                  {actionType === 'work_based_quote' && t('techservice.serviceActWorkQuote')}
                                  {!['status_change', 'quote_upload', 'quote_update', 'work_based_quote'].includes(actionType) && `${t('techservice.roleSupplier')}: ${actionType}`}
                                </>
                              ) : (
                                <>
                                  {actionType === 'create_service' && t('techservice.serviceActCreate')}
                                  {actionType === 'update_service' && t('techservice.serviceActUpdate')}
                                  {actionType === 'delete_service' && t('techservice.serviceActDelete')}
                                  {actionType === 'status_update' && t('techservice.serviceActStatusUpdate')}
                                  {log.formatted_description || actionType}
                                </>
                              )}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </span>
                        </div>
                        
                        {isEmailNotification && log.description && (
                          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                            <p className="font-medium mb-1">{t('techservice.serviceEmailDetails')}</p>
                            <p className="text-gray-600 dark:text-gray-400">{log.description}</p>
                            {log.new_value && (() => {
                              try {
                                const recipients = JSON.parse(log.new_value || '[]');
                                if (Array.isArray(recipients) && recipients.length > 0) {
                                  return (
                                    <details className="mt-2">
                                      <summary className="cursor-pointer text-xs text-primary hover:text-primary-dark">{t('techservice.showRecipients')}</summary>
                                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 pl-4">
                                        {recipients.map((recipient: any, idx: number) => (
                                          <div key={idx} className="mb-1">
                                            • {recipient.name} ({recipient.email}) - {recipient.type === 'contact' ? t('techservice.recipientContact') : recipient.type === 'technician' ? t('techservice.recipientTechnician') : t('techservice.recipientSupplier')}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  );
                                }
                              } catch (e) {
                                // JSON parse hatası, gösterilmez
                              }
                              return null;
                            })()}
                          </div>
                        )}
                        
                        {isSupplier && log.old_values && log.new_values && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">{log.old_values}</span> → 
                            <span className="font-medium text-green-600 ml-1">{log.new_values}</span>
                          </p>
                        )}
                        
                        {!isEmailNotification && (log.changed_fields || log.description || log.formatted_description) && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{log.changed_fields || log.description || log.formatted_description}</p>
                        )}
                        
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            {isSupplier ? '🏢' : '👤'} {log.user_name || log.display_user || t('techservice.roleSystem')} ({isSupplier ? t('techservice.roleSupplier') : log.user_role || t('techservice.roleSystem')})
                          </span>
                          {log.ip_address && (
                            <span>🌐 {log.ip_address}</span>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Tedarikçi İşlemleri */}
              {serviceHistory.supplier_actions && serviceHistory.supplier_actions.length > 0 && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-purple-600" />
                    {t('techservice.serviceHistorySupplierActionsTitle')}
                  </h3>
                  <div className="space-y-3">
                    {serviceHistory.supplier_actions.map((action: any) => (
                      <div key={action.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-purple-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-purple-500 rounded-full mr-3" />
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {action.action_type === 'status_change' && t('techservice.serviceHistActStatusChange')}
                              {action.action_type === 'quote_upload' && t('techservice.serviceHistActQuoteUpload')}
                              {action.action_type === 'work_description' && t('techservice.serviceHistActWorkDesc')}
                              {action.action_type === 'file_upload' && t('techservice.serviceHistActFileUpload')}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(action.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </span>
                        </div>
                        
                        {action.old_status && action.new_status && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">{action.old_status}</span> → 
                            <span className="font-medium text-green-600 ml-1">{action.new_status}</span>
                          </p>
                        )}
                        
                        {action.quote_amount && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">{t('techservice.serviceHistQuoteAmount')}</span> {action.quote_amount} {action.quote_currency}
                          </p>
                        )}
                        
                        {action.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{action.description}</p>
                        )}
                        

                        
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-2">
                          <span>🏢 {action.supplier_user_name} ({action.supplier_name})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Hiç veri yoksa */}
              {(!serviceHistory.service_logs || serviceHistory.service_logs.length === 0) &&
               (!serviceHistory.supplier_actions || serviceHistory.supplier_actions.length === 0) && (
                <div className="text-center py-8">
                  <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 dark:text-gray-400">{t('techservice.noServiceHistory')}</p>
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>

      {/* Label Settings Modal */}
      <Modal isOpen={showLabelSettingsModal} onClose={() => setShowLabelSettingsModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('techservice.labelSettingsTitle')}
              </h2>
            </div>

            <div className="p-3 sm:p-6 space-y-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.labelWidth')}
                  </label>
                  <input
                    type="number"
                    value={labelSettings.width}
                    onChange={(e) => setLabelSettings({...labelSettings, width: parseInt(e.target.value) || 100})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="50"
                    max="200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.labelHeight')}
                  </label>
                  <input
                    type="number"
                    value={labelSettings.height}
                    onChange={(e) => setLabelSettings({...labelSettings, height: parseInt(e.target.value) || 75})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="30"
                    max="150"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('techservice.labelOrientation')}
                </label>
                <select
                  value={labelSettings.orientation}
                  onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="portrait">{t('techservice.labelPortrait')}</option>
                  <option value="landscape">{t('techservice.labelLandscape')}</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-xs sm:text-sm">
                    {t('techservice.labelFontSize')}
                  </label>
                  <input
                    type="number"
                    value={labelSettings.fontSize}
                    onChange={(e) => setLabelSettings({...labelSettings, fontSize: parseInt(e.target.value) || 8})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="6"
                    max="16"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('techservice.labelMargin')}
                  </label>
                  <input
                    type="number"
                    value={labelSettings.margin}
                    onChange={(e) => setLabelSettings({...labelSettings, margin: parseInt(e.target.value) || 2})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="1"
                    max="10"
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('techservice.labelPresets')}</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setLabelSettings({width: 100, height: 75, orientation: 'portrait', fontSize: 8, margin: 2})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    Zebra ZT411 (100x75mm)
                  </button>
                  <button
                    onClick={() => setLabelSettings({width: 80, height: 60, orientation: 'portrait', fontSize: 7, margin: 2})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    {t('techservice.labelPresetStandard')}
                  </button>
                  <button
                    onClick={() => setLabelSettings({width: 120, height: 80, orientation: 'landscape', fontSize: 9, margin: 3})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    {t('techservice.labelPresetLarge')}
                  </button>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-3 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowLabelSettingsModal(false)}
                className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  setShowLabelSettingsModal(false);
                  showGlobalNotification.success(t('common.success'), t('techservice.labelSaved'));
                }}
                className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Reject Quote Modal */}
      <Modal isOpen={rejectModalService} onClose={() => setRejectModalService(null)}>
          {rejectModalService && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-3 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('techservice.rejectTitle')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 truncate">
                {t('techservice.rejectServiceNo')} <strong>{rejectModalService.service_number}</strong>
              </p>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('techservice.rejectReasonLabel')}
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder={t('techservice.rejectPlaceholder')}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                  rows={4}
                  required
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setRejectModalService(null);
                    setRejectionReason('');
                  }}
                  className="order-2 sm:order-1 px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors text-sm"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleRejectQuote}
                  disabled={!rejectionReason.trim()}
                  className="order-1 sm:order-2 px-4 py-2 min-h-[44px] bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {t('techservice.btnReject')}
                </button>
              </div>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default TechnicalServicePage;